import { Container } from "components/shared";
import { Button, Spinner } from "components/ui";
import { CompanyContext } from "contexts/CompanyContext";
import { useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import MediaElement from "views/calendar/MediaElement";
import useAvatars from "utils/hooks/useAvatars";
import { BsClock } from "react-icons/bs";
import CreateAvatarVideoCard from "components/avatars/CreateAvatarVideoCard";
import UpgradeBanner from "components/shared/UpgradeBanner";

const AIAvatarVideosBody = ({
    columnBreakPoints = { 0: 1, 768: 2, 1100: 3, 1400: 4 },
    showPreview = true,
    handleOnSelect = undefined,
    handleOnUnselect = undefined,
    selectedVideos = undefined,
    isSelectable = undefined,
    simplificated = false,
    showCreateButton = false,
    onClose = undefined,
    inModal = false
}) => {
    const { company } = useContext(CompanyContext);
    const {
        aiAvatarVideos,
        loadingAIVideos,
        aiVideosNextLink,
        pendingAIVideos,
        fetchAIAvatarVideos
    } = useAvatars();

    // Fetch AI Avatar videos on mount and company change
    useEffect(() => {
        if (company?.id) {
            fetchAIAvatarVideos(true);
        }
    }, [company?.id]);

    return (
        <div className={`w-full h-full ${simplificated ? 'flex flex-col' : 'flex flex-col'} gap-8`}>
            {(!!aiAvatarVideos?.length || loadingAIVideos) && 
                <Container className={`relative ${simplificated ? 'flex-1' : 'flex-1 overflow-y-auto'} min-h-full`}>
                    <div className={`relative flex flex-col gap-8 shrink-0 max-w-full ${simplificated ? 'lg:max-w-full' : 'lg:max-w-[250px]'} max-h-[512px] w-full ${1 == 1 && `!max-w-none !w-full !max-h-none`}`}>
                        {loadingAIVideos && !aiAvatarVideos?.length
                            ? <ResponsiveMasonry columnsCountBreakPoints={columnBreakPoints} className={`w-full transition-all`}>
                                <Masonry gutter='0.75rem'>
                                    {[1, 2, 3, 4, 5, 6].map(r => (
                                        <div key={r} className="aspect-video bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse"></div>
                                    ))}
                                </Masonry>
                              </ResponsiveMasonry>
                            : <ResponsiveMasonry columnsCountBreakPoints={columnBreakPoints} className={`w-full transition-all`}>
                                <Masonry gutter='1.5rem'>
                                    {/* Create Avatar Video Card - First Element */}
                                   {showCreateButton && <CreateAvatarVideoCard />}
                                    
                                    {aiAvatarVideos?.map((result, key) => {
                                        const isPending = pendingAIVideos.has(result.id) || result.status === 'pending' || result.status === 'processing';
                                        const video = result?.videos?.[0];
                                        const videoURL = result?.videos?.[0]?.url || result?.response?.split(",")?.[0];
                                        const videoThumbnailURL = result?.videos?.[0]?.thumbnail?.url || videoURL;
                                        
                                        if (isPending) {
                                            return (
                                                <div key={key} className="aspect-video bg-gray-300 dark:bg-gray-700 rounded-2xl animate-pulse flex items-center justify-center">
                                                    <div className="text-center">
                                                        <BsClock className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">Processing...</p>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        if (!videoURL || result?.status === 'failed') {
                                            return null;
                                        }
                                        
                                        return (
                                            <span 
                                                key={key}
                                                className={`${!!showPreview && `transition-all overflow-hidden`} !rounded-2xl`}
                                            >
                                                <MediaElement 
                                                    key={videoURL}
                                                    src={videoThumbnailURL}
                                                    originalSrc={videoURL}
                                                    handleOnSelect={handleOnSelect}
                                                    handleOnUnselect={handleOnUnselect}
                                                    selected={!!selectedVideos?.find(e => e.originalSrc == videoURL)} 
                                                    isSelectable={isSelectable}
                                                    isEditable={false}
                                                    mediaDescription={`Prompt: "${result?.query}"`}
                                                    aiImageModel={result?.model}
                                                    user={result?.user}
                                                    isVideo={true}
                                                    onError={(e) => {
                                                        try { e.target.parentElement.parentElement.style = "display: none" } catch (err) {}
                                                    }}
                                                />
                                            </span>
                                        );
                                    })}
                                </Masonry>
                              </ResponsiveMasonry>
                        }
                        {!!aiVideosNextLink && 
                            <div className="w-full flex justify-center mt-6">
                                <Button 
                                    variant="solid"
                                    onClick={() => fetchAIAvatarVideos(false)}
                                    loading={loadingAIVideos}
                                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                                >
                                    Load more
                                </Button>
                            </div>
                        }
                    </div>
                </Container>
            }

            {/* Empty State */}
            {!loadingAIVideos && !aiAvatarVideos?.length && (
                <div className="flex flex-col items-center justify-center py-16 px-4">
                    <div className="relative">
                        <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-2xl shadow-purple-500/50 mb-4">
                            <BsClock className="w-10 h-10 text-white" />
                        </div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        No AI Avatar Videos Yet
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-center max-w-md mb-6">
                        {!inModal 
                            ? "When you generate an AI Avatar video, it will be displayed here in your video library. Use the \"Create Your First Avatar Video\" button below to get started."
                            : "You haven't generated any AI Avatar videos yet. Generate your first video to see it here."
                        }
                    </p>
                    {!inModal ? (
                        <Link to="/ai-avatars-create">
                            <Button 
                                variant="solid"
                                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                            >
                                Create Your First Avatar Video
                            </Button>
                        </Link>
                    ) : (
                        onClose && (
                            <Button 
                                variant="solid"
                                onClick={onClose}
                                className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white"
                            >
                                Close
                            </Button>
                        )
                    )}
                </div>
            )}
        </div>
    )
}

export default AIAvatarVideosBody;

